import { useMemo } from 'react';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { createCompositeAdapter } from './compositeAdapter';
import { noopAdapter } from './noopAdapter';
import { createRxdAdapter } from './rxdAdapter';
import {
  _destroyScormAgainBackendForTests,
  createScormAgainAdapter
} from './scormAgainAdapter';
import {
  _resetLearnerLanguagePreferenceForTests,
  fetchLearnerLanguagePreference
} from './learnerLanguagePreference';
import { LmsAdapter } from './types';

let cached: LmsAdapter | null = null;

/**
 * Returns the singleton LMS adapter for the current page.
 *
 * Adapter *selection* is driven by two independent on/off flags from
 * `SequenceStore` (defaults: both enabled):
 *   - `sendRxdInteractions !== false` ⇒ wire up RXD
 *   - `sendScormAgainInteractions !== false` ⇒ wire up scorm-again
 *
 * Adapter *detection* (does the underlying LMS host actually exist?) is
 * NOT done here — each adapter detects its own host lazily on every call.
 * That mirrors the original preprod behavior where every component re-read
 * `window.parent.RXD` inline, and avoids the "RXD bootstrapped after us, so
 * we are stuck in noop forever" trap of eager detection.
 *
 * Truth table:
 *   | rxd flag | scorm-again flag | result               |
 *   |----------|------------------|----------------------|
 *   | true     | false            | rxd only             |
 *   | false    | true             | scorm-again only     |
 *   | true     | true             | composite[rxd, sa]   |
 *   | false    | false            | noop                 |
 */
export function getLmsAdapter(): LmsAdapter {
  if (cached) return cached;

  let rxdAllowed = true;
  let scormAgainAllowed = true;
  try {
    const state = useSequenceStore.getState();
    rxdAllowed = state.sendRxdInteractions !== false;
    scormAgainAllowed = state.sendScormAgainInteractions !== false;
  } catch {
    // Store not yet available (very early boot). Defaults above keep
    // both adapters wired up so per-call detection can still happen.
  }

  const adapters: LmsAdapter[] = [];
  if (rxdAllowed) adapters.push(createRxdAdapter());
  if (scormAgainAllowed) adapters.push(createScormAgainAdapter());

  if (adapters.length === 0) {
    cached = noopAdapter;
  } else if (adapters.length === 1) {
    // Skip the composite wrapper for the common single-adapter case so
    // diagnostics show the real adapter name (`rxd` / `scorm-again:…`)
    // rather than `composite[rxd]`.
    cached = adapters[0];
  } else {
    cached = createCompositeAdapter(adapters);
  }

  // One-line trail in the console makes it obvious which LMS host the SPA
  // actually wired up for this session — invaluable when triaging "why is
  // RXD silent" / "why does scorm-again time out" reports.
  console.info('[lms] active adapter:', cached.name);

  return cached;
}

/** Test-only escape hatch. Not exported through `index.ts` consumers. */
export function _resetLmsAdapterForTests(): void {
  cached = null;
  _destroyScormAgainBackendForTests();
  _resetLearnerLanguagePreferenceForTests();
}

export {
  fetchLearnerLanguagePreference,
  logLmsLanguage,
  peekLearnerLanguagePreference
} from './learnerLanguagePreference';

/**
 * Hook variant. The returned adapter is referentially stable across
 * renders, so it is safe to put into effect dependency arrays.
 */
export function useLmsAdapter(): LmsAdapter {
  return useMemo(() => getLmsAdapter(), []);
}

/**
 * Returns `true` when the active LMS adapter will actually record events.
 * Equivalent to "some backend will receive the data" — feature flags are
 * already baked into adapter selection by `getLmsAdapter()`, so the only
 * thing left to check is whether we landed on a real adapter or on noop.
 */
export function shouldSendInteractions(): boolean {
  return getLmsAdapter().isAvailable;
}

export type { LmsAdapter } from './types';
